import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  Phone, 
  Building, 
  ChevronDown, 
  ChevronUp,
  MessageSquare,
  History,
  AlertCircle
} from 'lucide-react';
import { useHubSpot } from '../hooks/useHubSpot';

const CustomerEngagementHistory = ({ customer }) => {
  const { getCustomerEngagements } = useHubSpot();
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedEngagements, setExpandedEngagements] = useState(new Set());

  useEffect(() => {
    if (customer?.id) {
      console.log('CustomerEngagementHistory: Customer ID detected, fetching engagements', customer.id);
      fetchEngagements();
    } else {
      console.log('CustomerEngagementHistory: No customer ID available', customer);
    }
  }, [customer?.id, getCustomerEngagements]);

  const fetchEngagements = async () => {
    if (!customer?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('CustomerEngagementHistory: Fetching engagements for customer', customer.id);
      
      // Check if the getCustomerEngagements function exists
      if (!getCustomerEngagements) {
        console.error('CustomerEngagementHistory: getCustomerEngagements function is not available');
        throw new Error('HubSpot engagements API not available');
      }
      
      const result = await getCustomerEngagements(customer.id, true);
      console.log('CustomerEngagementHistory: API response', result);
      
      if (result?.status === 'success' && result.engagements) {
        console.log('CustomerEngagementHistory: Setting engagements from API response', result.engagements);
        setEngagements(result.engagements);
      } else {
        console.log('CustomerEngagementHistory: No engagements found or invalid response', result);
        // For testing purposes, let's add a mock engagement if none are returned
        const mockEngagements = [{
          stored_data: {
            engagement_id: 12345,
            contact_id: "201",
            meeting_type: "Sales Call",
            notes: "This is a mock engagement for testing purposes",
            created_at: new Date().toISOString(),
            hubspot_created_at: Date.now()
          },
          contact_summary: {
            name: "John Smith",
            email: "john.smith@example.com",
            phone: "+1-555-123-4567",
            job_title: "Director of IT",
            company: customer.name
          }
        }];
        console.log('CustomerEngagementHistory: Adding mock engagement for testing', mockEngagements);
        setEngagements(mockEngagements);
      }
    } catch (err) {
      console.error('CustomerEngagementHistory: Error fetching engagements:', err);
      
      // For testing purposes, let's add a mock engagement on error too
      console.log('CustomerEngagementHistory: Adding mock engagement after error');
      const mockEngagements = [{
        stored_data: {
          engagement_id: 67890,
          contact_id: "202",
          meeting_type: "Error Recovery Demo",
          notes: "This is a mock engagement shown after an API error occurred",
          created_at: new Date().toISOString(),
          hubspot_created_at: Date.now()
        },
        contact_summary: {
          name: "Error Test User",
          email: "test@example.com",
          phone: "+1-555-987-6543",
          job_title: "Test Engineer",
          company: customer.name || "Unknown Company"
        }
      }];
      setEngagements(mockEngagements);
      
      // Set a more user-friendly error message
      setError(`Could not retrieve engagement history: ${err.message}`);
      // We're not clearing engagements here so we can see the mock data
    } finally {
      setLoading(false);
    }
  };

  const toggleEngagementExpansion = (engagementId) => {
    const newExpanded = new Set(expandedEngagements);
    if (newExpanded.has(engagementId)) {
      newExpanded.delete(engagementId);
    } else {
      newExpanded.add(engagementId);
    }
    setExpandedEngagements(newExpanded);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    const duration = (endTime - startTime) / (1000 * 60);
    return `${Math.round(duration)} min`;
  };

  // Format engagement for display (as per API documentation)
  const formatEngagementForDisplay = (engagement) => {
    const { stored_data, contact_summary, hubspot_engagement } = engagement;

    return {
      id: stored_data.engagement_id,
      type: stored_data.meeting_type,
      date: new Date(stored_data.created_at).toLocaleDateString(),
      notes: hubspot_engagement?.metadata?.body || stored_data.notes,
      contactPerson: {
        name: contact_summary?.name || 'Unknown',
        email: contact_summary?.email,
        phone: contact_summary?.phone,
        title: contact_summary?.job_title,
        company: contact_summary?.company
      },
      duration: formatDuration(
        hubspot_engagement?.metadata?.startTime,
        hubspot_engagement?.metadata?.endTime
      )
    };
  };

  const getEngagementTypeColor = (type) => {
    const colors = {
      'Sales Call': 'bg-blue-100 text-blue-800',
      'Follow-up': 'bg-green-100 text-green-800',
      'Demo': 'bg-purple-100 text-purple-800',
      'Discovery': 'bg-orange-100 text-orange-800',
      'Meeting': 'bg-gray-100 text-gray-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Previous Engagements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            <span className="ml-2 text-sm text-muted-foreground">Loading engagement history...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  // We'll show error message but still render mock data if available
  const hasErrorButData = error && engagements.length > 0;

  if (!engagements || engagements.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <History className="h-4 w-4" />
            Previous Engagements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <MessageSquare className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No previous engagements found</p>
            <p className="text-xs text-muted-foreground mt-1">
              This will show past meetings and calls
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Previous Engagements
          </div>
          <Badge variant="secondary" className="text-xs">
            {engagements.length} engagement{engagements.length !== 1 ? 's' : ''}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {/* Show error message but don't hide data if we have it */}
        {hasErrorButData && (
          <div className="mb-3 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
            <AlertCircle className="h-3 w-3 flex-shrink-0" />
            <span>API Error: {error} (showing mock data for testing)</span>
          </div>
        )}
        
        <ScrollArea className="h-64">
          <div className="space-y-3">
            {engagements.map((engagement, index) => {
              const { stored_data, contact_summary, hubspot_engagement } = engagement;
              const isExpanded = expandedEngagements.has(stored_data.engagement_id);
              const notes = hubspot_engagement?.metadata?.body || stored_data.notes;
              const duration = formatDuration(
                hubspot_engagement?.metadata?.startTime,
                hubspot_engagement?.metadata?.endTime
              );

              return (
                <div key={stored_data.engagement_id} className="border rounded-lg p-3 space-y-2">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${getEngagementTypeColor(stored_data.meeting_type)}`}
                        >
                          {stored_data.meeting_type}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          {formatDate(stored_data.created_at)}
                        </div>
                        {duration && (
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {duration}
                          </div>
                        )}
                      </div>
                      
                      {/* Contact Info */}
                      {contact_summary && (
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {contact_summary.name}
                            {contact_summary.job_title && (
                              <span className="ml-1">({contact_summary.job_title})</span>
                            )}
                          </div>
                          {contact_summary.email && (
                            <div className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {contact_summary.email}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {notes && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => toggleEngagementExpansion(stored_data.engagement_id)}
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3" />
                        ) : (
                          <ChevronDown className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </div>

                  {/* Expanded Notes */}
                  {isExpanded && notes && (
                    <>
                      <Separator />
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs font-medium text-muted-foreground">Meeting Notes</span>
                        </div>
                        <div className="text-sm bg-muted/50 rounded-md p-2">
                          {notes}
                        </div>
                      </div>
                    </>
                  )}

                  {index < engagements.length - 1 && <Separator className="mt-3" />}
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default CustomerEngagementHistory;